  import axios from 'axios';
  import { API_CONFIG, AUTH_CONFIG } from './config';

  /**
   * Generate a unique session ID
   * @returns {string} A unique session ID
   */
  export const generateSessionId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  };

  /**
   * Get or create a session ID
   * @returns {string} The current session ID
   */
  export const getSessionId = () => {
    let sessionId = localStorage.getItem(AUTH_CONFIG.SESSION_KEY);
    if (!sessionId) {
      sessionId = generateSessionId();
      localStorage.setItem(AUTH_CONFIG.SESSION_KEY, sessionId);
    }
    return sessionId;
  };

  // Create axios instance with default config
  const apiClient = axios.create({
    baseURL: API_CONFIG.API_URL,
    headers: API_CONFIG.DEFAULT_HEADERS,
    timeout: API_CONFIG.TIMEOUT,
  });

  // Track if we're currently refreshing the page to prevent redirect loops
  let isPageRefresh = false;

  // Set the refresh flag when the page is loading
  if (document.readyState !== 'complete') {
    isPageRefresh = true;
    // Reset the flag after the page has fully loaded
    window.addEventListener('load', () => {
      setTimeout(() => {
        isPageRefresh = false;
      }, 1000); // Give a small delay to ensure all initial requests are handled as part of the refresh
    });
  }

  // Add request interceptor to include auth token in requests
  apiClient.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
        // Add session ID to all authenticated requests
        config.headers['X-Session-ID'] = getSessionId();
      }
      
      // Append showPrintRemark for print endpoints to ensure setting applies globally
      if (config.url && config.url.includes('/print/')) {
        const showPrintRemark = localStorage.getItem('showPrintRemark');
        if (showPrintRemark !== null) {
          const separator = config.url.includes('?') ? '&' : '?';
          config.url = `${config.url}${separator}showPrintRemark=${showPrintRemark}`;
        }
      }
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add response interceptor to handle auth errors and modify print responses
  apiClient.interceptors.response.use(
    (response) => {
      // Inject hide remarks logic into print templates to ensure it hides even if backend ignores query param
      if (response.config && response.config.url && response.config.url.includes('/print/')) {
        const showPrintRemark = localStorage.getItem('showPrintRemark') !== 'false';
        if (!showPrintRemark && response.data) {
          if (typeof response.data.css === 'string') {
            response.data.css += `\n.notes-section, .remark-section, .invoice-notes, .invoice-remark, .print-notes, .print-remark, .notes, .remark, #notes, #remark { display: none !important; }`;
          }
          if (typeof response.data.html === 'string') {
            const hideScript = `
              <script>
                try {
                  var elements = document.querySelectorAll('div, p, span, td, th, h1, h2, h3, h4, h5, h6, strong, b');
                  for (var i = 0; i < elements.length; i++) {
                    var el = elements[i];
                    var text = (el.innerText || el.textContent || '').trim().toLowerCase();
                    if ((text === 'notes' || text === 'remarks' || text === 'note' || text === 'remark' || text === 'note:' || text === 'notes:' || text === 'remark:' || text === 'remarks:') && el.children.length === 0) {
                      var tr = el.closest('tr');
                      if (tr) {
                        tr.style.display = 'none';
                        var nextTr = tr.nextElementSibling;
                        if (nextTr) nextTr.style.display = 'none';
                      } else {
                        var parent = el.parentElement;
                        if (parent) parent.style.display = 'none';
                        var nextSib = parent ? parent.nextElementSibling : el.nextElementSibling;
                        if (nextSib) nextSib.style.display = 'none';
                      }
                    }
                  }
                } catch(e) {}
              </script>
            `;
            response.data.html += hideScript;
          }
        }
      }
      return response;
    },
    (error) => {
      // Skip auth error handling during page refresh
      if (isPageRefresh && error.response && error.response.status === 401) {
        console.log('Ignoring auth error during page refresh');
        return Promise.reject(error);
      }
      
      // Handle session invalidated error
      if (error.response && error.response.status === 401) {
        // Only handle auth errors for actual API requests, not for auth checks
        if (error.config.url.includes('/auth/current-user')) {
          console.log('Auth check failed, but not redirecting during page initialization');
          return Promise.reject(error);
        }
        
        if (error.response.data && error.response.data.code === 'SESSION_INVALIDATED') {
          // Clear auth data
          localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);
          localStorage.removeItem(AUTH_CONFIG.USER_KEY);
          localStorage.removeItem(AUTH_CONFIG.TENANT_KEY);
          localStorage.removeItem(AUTH_CONFIG.SESSION_KEY);
          
          // Show message to user
          alert('Your session has been invalidated because you logged in from another device or browser. Please log in again.');
          
          // Only redirect to login if not already on login page
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }
        
        // For other 401 errors on regular API calls (not during refresh or auth check)
        console.log('Authentication error on API call:', error.config.url);
      }
      return Promise.reject(error);
    }
  );

  export default apiClient;
